import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { TaxRate } from '../types';
import Select from '../../../components/ui/Select';

interface CurrentCalculationsProps {
  taxRates: TaxRate[];
}

const CurrentCalculations = ({ taxRates }: CurrentCalculationsProps) => {
  const [selectedUF, setSelectedUF] = useState<string>('all');
  const [selectedCFOP, setSelectedCFOP] = useState<string>('all');

  const uniqueUFs = Array.from(new Set(taxRates.map((rate) => rate.uf))).sort();
  const uniqueCFOPs = Array.from(new Set(taxRates.map((rate) => rate.cfop))).sort();

  const ufOptions = [
    { value: 'all', label: 'Todas as UFs' },
    ...uniqueUFs.map((uf) => ({ value: uf, label: uf }))
  ];

  const cfopOptions = [
    { value: 'all', label: 'Todos os CFOPs' },
    ...uniqueCFOPs.map((cfop) => ({ value: cfop, label: cfop }))
  ];

  const filteredRates = taxRates.filter((rate) => {
    const ufMatch = selectedUF === 'all' || rate.uf === selectedUF;
    const cfopMatch = selectedCFOP === 'all' || rate.cfop === selectedCFOP;
    return ufMatch && cfopMatch;
  });

  const averageRate =
    filteredRates.length > 0
      ? filteredRates.reduce((sum, rate) => sum + rate.rate, 0) / filteredRates.length
      : 0;

  const maxRate = filteredRates.length > 0 ? Math.max(...filteredRates.map((r) => r.rate)) : 0;
  const minRate = filteredRates.length > 0 ? Math.min(...filteredRates.map((r) => r.rate)) : 0;

  const exemptionCount = filteredRates.filter(
    (rate) => rate.exemptionConditions && rate.exemptionConditions.length > 0
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Select
            label="Filtrar por UF"
            options={ufOptions}
            value={selectedUF}
            onChange={setSelectedUF}
          />
        </div>
        <div className="flex-1">
          <Select
            label="Filtrar por CFOP"
            options={cfopOptions}
            value={selectedCFOP}
            onChange={setSelectedCFOP}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-muted-foreground">Alíquota Média</span>
          </div>
          <p className="text-2xl font-bold text-foreground font-mono">{averageRate.toFixed(2)}%</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="ArrowUp" size={18} color="var(--color-error)" />
            <span className="text-sm font-medium text-muted-foreground">Alíquota Máxima</span>
          </div>
          <p className="text-2xl font-bold text-error font-mono">{maxRate.toFixed(2)}%</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="ArrowDown" size={18} color="var(--color-success)" />
            <span className="text-sm font-medium text-muted-foreground">Alíquota Mínima</span>
          </div>
          <p className="text-2xl font-bold text-success font-mono">{minRate.toFixed(2)}%</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle2" size={18} color="var(--color-accent)" />
            <span className="text-sm font-medium text-muted-foreground">Com Isenção</span>
          </div>
          <p className="text-2xl font-bold text-accent font-mono">{exemptionCount}</p>
        </div>
      </div>

      <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={18} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground mb-1">Informação sobre Cálculos</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              As alíquotas apresentadas são aplicadas automaticamente durante o processamento da consulta tributária.
              Os valores consideram a legislação vigente e são atualizados em tempo real através da integração com o
              Gestor Tributário 3.0.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Detalhamento por Configuração</h4>
        {filteredRates.slice(0, 5).map((rate, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">UF:</span>
                <span className="font-semibold text-foreground">{rate.uf}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">CFOP:</span>
                <span className="font-mono text-sm text-foreground">{rate.cfop}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">CST:</span>
                <span className="font-mono text-sm text-foreground">{rate.cst}</span>
              </div>
            </div>
            <span className="font-semibold font-mono text-primary">{rate.rate.toFixed(2)}%</span>
          </div>
        ))}
        {filteredRates.length > 5 && (
          <p className="text-sm text-muted-foreground text-center">
            E mais {filteredRates.length - 5} configurações...
          </p>
        )}
      </div>
    </div>
  );
};

export default CurrentCalculations;